import { Box, Breadcrumbs, IconButton, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from 'mdi-react/ArrowBackIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.25, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(0.5),
  },
  title: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  breadcrumbs: {
    flex: 1,
    minWidth: 0,
    '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
    '& .MuiBreadcrumbs-li': { minWidth: 0 },
  },
  crumbLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 400,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  close: {
    color: theme.palette.text.primary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
});

const ChatFlowContextHeader = ({ title, breadcrumbs, actions = null }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { context } = useContext(ChatFlowContext);
  const showBack = Boolean(context?.length > 1 && title && !breadcrumbs?.length);

  if (!title && !breadcrumbs?.length) {
    return null;
  }

  return (
    <Box sx={styles.root} component="header">
      <Box sx={styles.headerLeft}>
        {showBack && (
          <IconButton
            sx={styles.close}
            edge="start"
            aria-label="back"
            onClick={context.pop}
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {breadcrumbs?.length > 0 ? (
          <Breadcrumbs sx={styles.breadcrumbs} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const key = crumb.key || `crumb-${index}`;
              if (isLast || !crumb.onClick) {
                return (
                  <Typography
                    key={key}
                    component="span"
                    noWrap
                    aria-label={crumb.ariaLabel}
                    sx={[
                      {
                        color: isLast ? 'text.primary' : 'text.secondary',
                        fontWeight: isLast ? 500 : 400,
                      },
                      styles.crumbLink,
                    ]}
                  >
                    {crumb.icon}
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link
                  key={key}
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  color="inherit"
                  onClick={crumb.onClick}
                  sx={styles.crumbLink}
                  title={crumb.title}
                  aria-label={crumb.ariaLabel || crumb.title}
                >
                  {crumb.icon}
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        ) : (
          <Typography sx={styles.title} noWrap>
            {title}
          </Typography>
        )}
      </Box>
      {actions ? (
        <Box sx={styles.actions}>{actions}</Box>
      ) : (
        context?.clear && (
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label={I18n.t('Common.close') || 'Close'}
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        )
      )}
    </Box>
  );
};

ChatFlowContextHeader.propTypes = {
  title: PropTypes.string,
  actions: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      onClick: PropTypes.func,
      icon: PropTypes.node,
      title: PropTypes.string,
      ariaLabel: PropTypes.string,
      key: PropTypes.string,
    }),
  ),
};

export default ChatFlowContextHeader;
